import { ChoiceGroup, Panel, PanelType, Spinner } from "@fluentui/react";
import {
  getRolloutFooter,
  parseStageType,
  renderField,
} from "./RolloutFormFieldRenderer";
import { useEffect, useMemo, useState } from "react";
import { styles } from "./rolloutDetailsForm.styles";
import { useFetchRolloutTemplates } from "../../../Hooks/AdoHooks/UseFetchRolloutTemplates";
import { validateRolloutField } from "./rolloutValidation";
import { cleanRolloutParams } from "../rolloutDetails.utils";
import {
  RolloutAndProgressionType,
  RolloutDetailsType,
  RolloutTemplate,
  RolloutTemplateParameter,
} from "../../../Types";
import { postRolloutDetails } from "../../../Api/ado.api";
import { useEv2ExperimentParams } from "../../../Hooks/UseCurrentExtension";
import { useFetchPipelineId } from "../../../Hooks/AdoHooks/UseFetchPipelineId";
import useAppInsights from "../../../Hooks/UseAppInsights";
import debounce from "lodash/debounce";
import { SeverityLevel } from "@microsoft/applicationinsights-web";

interface RolloutDetailsFormProps {
  /**
   * Callback used when rollout is successfully submitted
   */
  onRolloutSubmitted: () => void;
  /**
   * The configuration specification filename for the rollout
   */
  configSpecFilename: string;
  /**
   * Callback used when user cancels the form
   */
  onCancel: () => void;
}

const ignoreParams = ["ConfigSpecPath", "ConfigSpecFileName"];

/**
 * A form component for creating and submitting new experiment rollouts
 * Allows users to select rollout templates and configure parameters for deployment
 */
const RolloutDetailsForm: React.FC<RolloutDetailsFormProps> = ({
  onRolloutSubmitted,
  configSpecFilename,
  onCancel,
}) => {
  const { extensionName, extensionFolder } = useEv2ExperimentParams();
  const { trackOperationEvent, trackExceptionEvent, createOperationTracker } =
    useAppInsights("RolloutDetailsForm");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pipelineError, setPipelineError] = useState<string | null>(null);
  const [error, setError] = useState(null);
  const [startWiggle, setStartWiggle] = useState(false);
  const [formValues, setFormValues] = useState<RolloutDetailsType>(
    DefaultRolloutDetails,
  );

  const {
    value: templates,
    templateMap,
    setSelectedRolloutTemplate,
    selectedRolloutTemplate,
    loading: isLoading,
  } = useFetchRolloutTemplates(extensionName, setError);

  const { value: pipelineId } = useFetchPipelineId(
    selectedRolloutTemplate?.text ?? "",
    setPipelineError,
  );

  const selectedTemplate = useMemo(
    () =>
      (templateMap &&
        selectedRolloutTemplate &&
        selectedRolloutTemplate.key &&
        templateMap[selectedRolloutTemplate.key]) ||
      null,
    [templateMap, selectedRolloutTemplate],
  );

  const handleCancel = () => {
    // Reset form values to defaults for the selected template
    if (selectedTemplate && selectedTemplate.parameters) {
      const extensionPath = extensionFolder?.extensionRepoPath;
      if (!extensionFolder) {
        setPipelineError(
          "Extension data is missing: Unable to find extension path.",
        );
        return;
      }
      const initial: RolloutDetailsType = DefaultRolloutDetails;
      selectedTemplate.parameters.forEach((param: RolloutTemplateParameter) => {
        initial[param.name] = param.default;
      });
      setFormValues({
        ...initial,
        ConfigSpecFileName: configSpecFilename,
        ConfigSpecPath: `Extensions/${extensionPath}`,
      });
    }
    if (onCancel) onCancel();
  };

  const handleStartExperiment = async () => {
    const { isValid } = validateAllFields();
    if (!isValid) {
      setStartWiggle(true);
      setTimeout(() => setStartWiggle(false), 600);
      return;
    }
    setIsSubmitting(true);
    setPipelineError(null);
    if (!pipelineId) {
      setPipelineError("No pipeline configured for this extension.");
      setIsSubmitting(false);
      return;
    }
    try {
      // Clean up params before submit
      const cleanedParams = cleanRolloutParams(formValues);
      const tracker = createOperationTracker(
        "Rollout",
        "handleStartExperiment",
      );
      tracker.start({ cleanedParams });
      try {
        await postRolloutDetails(pipelineId, cleanedParams);
        tracker.succeed();
      } catch (err: any) {
        tracker.fail(err.message, { pipelineId, formValues });
        throw err;
      }
      onRolloutSubmitted();
    } catch (err: any) {
      trackExceptionEvent(
        err.message || "Failed to start pipeline",
        SeverityLevel.Error,
        {
          function: "handleStartExperiment",
          properties: { pipelineId, formValues },
        },
      );
      setPipelineError(err.message || "Failed to start pipeline");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Validate all fields before submit
  const validateAllFields = () => {
    if (!selectedTemplate || !selectedTemplate.parameters) {
      return { isValid: true, errors: {} };
    }
    const errors: Record<string, string> = {};
    selectedTemplate.parameters.forEach((param: RolloutTemplateParameter) => {
      if (ignoreParams.includes(param.name)) return;
      const error = validateRolloutField(
        param.name,
        formValues[param.name],
        selectedTemplate.parameters,
      );
      if (error) errors[param.name] = error;
      else errors[param.name] = "";
    });
    const isValid = Object.values(errors).every((e) => !e);
    return {
      isValid,
      errors,
    };
  };

  const debounceValidateAndTrack = useMemo(
    () =>
      debounce((name: string, value: any, initialValue: any) => {
        const error = validateRolloutField(
          name,
          value,
          selectedTemplate?.parameters,
        );
        trackOperationEvent({
          operation: "FormChange",
          function: "handleFieldChange",
          properties: {
            field: name,
            value,
            initialValue,
            error,
            isValid: !error,
          },
        });
      }, 500),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- for debounce
    [],
  );

  const handleFieldChange = (name: string, value: any) => {
    const initialValue = formValues[name];
    setFormValues((prev) => ({ ...prev, [name]: value }));
    // Validate this field
    debounceValidateAndTrack(name, value, initialValue);
  };

  // Initialize form values when template changes
  useEffect(() => {
    if (selectedTemplate && selectedTemplate.parameters) {
      const initial: RolloutDetailsType = DefaultRolloutDetails;
      selectedTemplate.parameters.forEach((param: RolloutTemplateParameter) => {
        initial[param.name] = param.default;
      });
      const extensionPath = extensionFolder?.extensionRepoPath;
      if (!extensionFolder) {
        setPipelineError(
          "Extension data is missing: Unable to find extension path.",
        );
        return;
      }
      setFormValues({
        ...initial,
        ConfigSpecFileName: configSpecFilename,
        ConfigSpecPath: `Extensions/${extensionPath}`,
        ...defaultOverrides,
      });
    }
  }, [configSpecFilename, extensionFolder, selectedTemplate]);

  return (
    <Panel
      isOpen={true}
      onDismiss={handleCancel}
      headerText="Start New Rollout"
      closeButtonAriaLabel="Close"
      type={PanelType.medium}
      isFooterAtBottom={true}
      onRenderFooter={() =>
        getRolloutFooter(handleCancel, handleStartExperiment, startWiggle)
      }
      isLightDismiss
    >
      <div className={styles.rolloutDetailsFormPanelContent}>
        {/* Error area for pipeline errors */}
        {pipelineError && (
          <div className={styles.rolloutDetailsFormError}>{pipelineError}</div>
        )}
        {!isLoading && error && (
          <div className={styles.rolloutDetailsFormError}>
            Error loading templates
          </div>
        )}
        {!isLoading && (!templates || templates.length === 0) && (
          <div>No rollout templates found.</div>
        )}
        {(isLoading || !selectedTemplate) && (
          <div className={styles.rolloutDetailsFormLoadingSpinner}>
            <Spinner label="Loading rollout templates..." />
          </div>
        )}
        <div className={styles.rolloutDetailsFormContainer}>
          {!isLoading && templates && templates.length > 0 && (
            <div className={styles.rolloutDetailsFormFields}>
              <ChoiceGroup
                label="Select a rollout template"
                className={styles.rolloutDetailsTemplateChoice}
                options={templates.map((template: RolloutTemplate) => ({
                  key: template.__filename,
                  text: parseStageType(template.__filename),
                }))}
                selectedKey={selectedRolloutTemplate?.key}
                onChange={(_, option) => {
                  if (option) {
                    setSelectedRolloutTemplate(option);
                    trackOperationEvent({
                      operation: "TemplateSelected",
                      function: "ChoiceGroup.onChange",
                      properties: { selectedTemplate: option.key },
                    });
                  }
                }}
                required
              />
              {/* Parameter form */}
              {selectedTemplate && selectedTemplate.parameters && (
                <form className={styles.rolloutDetailsFormFields}>
                  {selectedTemplate.parameters
                    .filter(
                      (param: RolloutTemplateParameter) =>
                        !ignoreParams.includes(param.name),
                    )
                    .map((param: RolloutTemplateParameter) => {
                      return (
                        <div
                          key={param.name}
                          className={styles.rolloutDetailsFormField}
                        >
                          {renderField(
                            param,
                            formValues[param.name],
                            handleFieldChange,
                          )}
                        </div>
                      );
                    })
                    .filter(Boolean)}
                </form>
              )}
            </div>
          )}
        </div>
        {/* Loading overlay when submitting */}
        {isSubmitting && (
          <div className={styles.rolloutDetailsFormOverlay}>
            <Spinner label="Starting experiment rollout..." />
          </div>
        )}
      </div>
    </Panel>
  );
};

const DefaultRolloutDetails: RolloutDetailsType = {
  ConfigSpecFileName: "",
  ConfigSpecPath: "",
  RolloutAndProgressionType: RolloutAndProgressionType.RC_SDP,
  ValidateOnly: false,
  EnableHealthChecks: false,
};
const defaultOverrides: Partial<RolloutDetailsType> = {
  EnableHealthChecks: false,
};
export default RolloutDetailsForm;
